import React, { useRef, useEffect, useState } from 'react';
import styles from './HowItWorksSection.module.scss';
import { motion } from 'framer-motion';

const HowItWorksSection = () => {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      if (rect.top < window.innerHeight - 100) setVisible(true);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-advance active step every 3 seconds when section is visible
  useEffect(() => {
    if (!visible) return;
    
    const interval = setInterval(() => {
      setActiveStep(prev => prev === 6 ? 1 : prev + 1);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [visible]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const numberVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.7,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const lineVariants = {
    hidden: { height: 0 },
    visible: { 
      height: "100%",
      transition: { 
        duration: 1.0,
        ease: "easeInOut",
        delay: 0.3
      }
    }
  };
  
  const getStepDetails = (stepNumber) => {
    const steps = [
      {
        title: "Curated Properties",
        description: "Buyers agents curate investment grade properties.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 9.3V4h-3v2.6L12 3 2 12h3v8h5v-6h4v6h5v-8h3l-3-2.7zm-9 .7c0-1.1.9-2 2-2s2 .9 2 2h-4z" fill="currentColor" />
          </svg>
        )
      },
      {
        title: "100% Value for Sellers",
        description: "Sellers receive 100% of property value.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor" />
          </svg>
        )
      },
      {
        title: "5,000 Unique Pieces",
        description: "5,000 unique pieces sold per property.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm13 9H8v-2h8v2zm0-4H8V8h8v2z" fill="currentColor" />
          </svg>
        )
      },
      {
        title: "1-Click Purchase",
        description: "Streamlined 1-Click purchase experience.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 2H7c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 18H8c-.55 0-1-.45-1-1s.45-1 1-1h8c.55 0 1 .45 1 1s-.45 1-1 1zm1-3H7V4h10v13z" fill="currentColor" />
          </svg>
        )
      },
      {
        title: "Rental Income",
        description: "Earn net rental income.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor" />
          </svg>
        )
      },
      {
        title: "Professional Management",
        description: "Professional, independent property management.",
        icon: (
          <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor" />
          </svg>
        )
      }
    ];
    
    return steps[stepNumber - 1];
  };

  return (
    <section ref={ref} className={styles.section}>
      <div className={`${styles.card} ${visible ? styles.visible : ''}`}>
        <div className={styles.contentWrapper}>
          <motion.h2 
            className={styles.title}
            initial={{ opacity: 0, y: -15 }}
            animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: -15 }}
            transition={{ duration: 0.8 }}
          >
            How It Works
          </motion.h2>
          
          <motion.div 
            className={styles.howItWorksContainer}
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <div className={styles.stepsTimeline}>
              <motion.div 
                className={styles.stepsContainer}
                initial="hidden"
                animate={visible ? "visible" : "hidden"}
                variants={containerVariants}
              >
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <motion.div 
                    key={step}
                    className={`${styles.step} ${activeStep === step ? styles.activeStep : ''}`}
                    variants={itemVariants}
                    onClick={() => setActiveStep(step)}
                  >
                    <div className={styles.stepContent}>
                      <motion.div 
                        className={styles.stepNumber} 
                        variants={numberVariants}
                      >
                        {step}
                      </motion.div>
                      <h3 className={styles.stepTitle}>{getStepDetails(step).title}</h3>
                      <p className={styles.stepDescription}>
                        {getStepDetails(step).description}
                      </p>
                    </div>
                    {step < 6 && (
                      <motion.div 
                        className={`${styles.stepLine} ${activeStep > step ? styles.completedLine : ''}`} 
                        variants={lineVariants}
                      ></motion.div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            </div>
            
            <motion.div 
              className={styles.stepDetailPanel}
              initial={{ opacity: 0, x: 20 }}
              animate={visible ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <motion.div 
                className={styles.stepIconContainer}
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, 0, -5, 0]
                }}
                transition={{
                  duration: 1.5,
                  ease: "easeInOut",
                  times: [0, 0.25, 0.5, 0.75, 1],
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              >
                {getStepDetails(activeStep).icon}
              </motion.div>
              
              <h3 className={styles.stepDetailTitle}>
                {getStepDetails(activeStep).title}
              </h3>
              
              <p className={styles.stepDetailDescription}>
                {getStepDetails(activeStep).description}
              </p>
              
              {/* <div className={styles.stepIndicators}>
                {[1, 2, 3, 4, 5, 6].map(step => (
                  <button 
                    key={step}
                    className={`${styles.stepIndicator} ${activeStep === step ? styles.activeIndicator : ''}`}
                    onClick={() => setActiveStep(step)}
                    aria-label={`View step ${step}`}
                  />
                ))}
              </div> */}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection; 